export type WhClass =
  | "C1"
  | "C2"
  | "C3"
  | "C4"
  | "C5"
  | "C6"
  | "HS"
  | "LS"
  | "Null"
  | "Thera"
  | "C13"
  | "Drifter"
  | "Pochven";

export const JSPACE_CLASSES: ReadonlySet<WhClass> = new Set([
  "C1",
  "C2",
  "C3",
  "C4",
  "C5",
  "C6",
  "C13",
  "Thera",
  "Drifter",
]);

export const KSPACE_CLASSES: ReadonlySet<WhClass> = new Set([
  "HS",
  "LS",
  "Null",
]);

export function isJspaceClass(c: WhClass): boolean {
  return JSPACE_CLASSES.has(c);
}

export function isKspaceClass(c: WhClass): boolean {
  return KSPACE_CLASSES.has(c);
}

export type WhEffect =
  | "Pulsar"
  | "Magnetar"
  | "WolfRayet"
  | "BlackHole"
  | "CataclysmicVariable"
  | "RedGiant"
  | null;

export type ConnectionLifetime = "fresh" | "EOL" | "VEOL";
export type ConnectionMassState = "stable" | "destab" | "critical";
export type ConnectionDirection = "unknown" | "out" | "in";

export type SignatureGroup =
  | "wormhole"
  | "relic"
  | "data"
  | "gas"
  | "combat"
  | "ore"
  | "unknown";

export interface Signature {
  scan_id: string;
  name: string;
  group: SignatureGroup;
  wh_type?: string | null;
}

export interface Static {
  code: string;
  class: WhClass;
}

export type TradeHub = "Jita" | "Amarr" | "Dodixie" | "Rens" | "Hek";

export type HubJumps = Record<TradeHub, number | null>;

export type SystemHostility = "friendly" | "pvp_friendly" | "hostile";
export type SystemActivity = "active" | "dormant";

export type StructureKind =
  | "Astrahus"
  | "Fortizar"
  | "Keepstar"
  | "Athanor"
  | "Tatara"
  | "Raitaru"
  | "Azbel"
  | "Sotiyo"
  | "POS"
  | "other";

export interface SystemStructure {
  structure_id: number;
  kind: StructureKind;
  name: string;
  updated_at: string;
}

export interface SystemOwner {
  entity_type: "corp" | "alliance";
  entity_id: number;
  name: string | null;
  added_at: string;
}

export interface System {
  id: string;
  notes?: string;
  alias?: string;
  hostility?: SystemHostility;
  activity?: SystemActivity;
  annotations_updated_at?: string;
  structures?: SystemStructure[];
  owners?: SystemOwner[];
  position?: { x: number; y: number } | null;
  signatures: Signature[];
}

export interface StaticSystem {
  id: string;
  name: string;
  class: WhClass;
  statics?: Static[];
  effect: WhEffect;
  is_shattered?: boolean;
  security?: number;
  region?: string;
  hub_jumps?: HubJumps;
}

export type WanderingByClass = Record<WhClass, string[]>;

export type WhDestinationByCode = Record<string, WhClass>;

export interface StaticSystemsResponse {
  systems: Record<string, StaticSystem>;
  wandering_by_class: WanderingByClass;
  wh_destination_by_code: WhDestinationByCode;
}

export interface Connection {
  id: string;
  from: string;
  to: string;
  wh_type: string | null;
  lifetime: ConnectionLifetime;
  mass: ConnectionMassState;
  direction: ConnectionDirection;
}

export interface Pilot {
  character_id: number;
  name: string;
  ship_type_name?: string | null;
}

export type PresenceBySystem = Record<string, Pilot[]>;

export type ChainAccess = "private" | "corp" | "alliance" | "public";

export const ACCESS_TIERS: ChainAccess[] = ["private", "corp", "alliance", "public"];

export const ACCESS_LABELS: Record<ChainAccess, string> = {
  private: "Private",
  corp: "Corp",
  alliance: "Alliance",
  public: "Public",
};

export type ChainType = "standard" | "day_trip";

export interface ChainOwner {
  character_id: number;
  name: string;
}

export interface ChainSummary {
  id: string;
  name: string;
  root_system_id: string;
  access: ChainAccess;
  chain_type: ChainType;
  owner: ChainOwner;
  is_owner: boolean;
}

export interface Chain extends ChainSummary {
  systems: System[];
  connections: Connection[];
}
